/**
 * Authenticated fetch shared by the typed client and raw calls (uploads, downloads).
 * - adds the in-memory access token (never stored in localStorage)
 * - on 401 refreshes once through the httpOnly refresh cookie and retries the request
 * - when the refresh fails the session is over: listeners (SessionProvider) switch to logged-out
 * - 503 from the API/nginx during maintenance is reported to listeners as well
 */
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const XHR = { 'X-Requested-With': 'XMLHttpRequest' };

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
const expiredListeners = new Set<() => void>();
const maintenanceListeners = new Set<() => void>();

export const tokens = {
  get: () => accessToken,
  set: (t: string | null) => {
    accessToken = t;
  },
  onExpired(fn: () => void) {
    expiredListeners.add(fn);
    return () => expiredListeners.delete(fn);
  },
  onMaintenance(fn: () => void) {
    maintenanceListeners.add(fn);
    return () => maintenanceListeners.delete(fn);
  },
};

/** Exchange the refresh cookie for a new access token (deduplicated across concurrent 401s). */
/** user that came with the last refresh (GET /auth/me shape), consumed by the session bootstrap */
let refreshedUser: unknown = null;
export const takeRefreshedUser = <T>(): T | null => {
  const u = refreshedUser as T | null;
  refreshedUser = null;
  return u;
};

/** a session may exist (companion flag of the httpOnly refresh cookie, set by the API) */
export const hasSessionFlag = (): boolean =>
  /(?:^|;\s*)mantis_session=1(?:;|$)/.test(document.cookie);

type RefreshBody = { accessToken: string; user?: unknown } | null;
/** the refresh index.html already started while the bundle was downloading (used once) */
function takeBootRefresh(): Promise<RefreshBody> | null {
  const w = window as { __mantisBoot?: Promise<RefreshBody> };
  const p = API_BASE === '' ? (w.__mantisBoot ?? null) : null;
  delete w.__mantisBoot;
  return p;
}

export function refreshAccessToken(): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const boot = takeBootRefresh();
      let body: RefreshBody;
      if (boot) body = await boot;
      else {
        const r = await fetch(`${API_BASE}/api/v2/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: XHR,
        });
        body = r.ok ? ((await r.json()) as RefreshBody) : null;
      }
      if (!body) return null;
      const { accessToken: t, user } = body;
      accessToken = t;
      refreshedUser = user ?? null;
      return t;
    } catch {
      return null;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

const isAuthCall = (url: string) =>
  /\/api\/v2\/auth\/(login|refresh|logout|register|activate|password\/(forgot|reset))/.test(url);

export async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  if (accessToken && !input.headers.has('Authorization'))
    input.headers.set('Authorization', `Bearer ${accessToken}`);
  let res = await fetch(input);
  if (res.status === 401 && !isAuthCall(input.url)) {
    const t = await refreshAccessToken();
    if (t) {
      // session is alive: a second 401 is the answer to this request (e.g. wrong current
      // password on /auth/password/change), not an expired login
      retry.headers.set('Authorization', `Bearer ${t}`);
      res = await fetch(retry);
    } else {
      accessToken = null;
      expiredListeners.forEach((fn) => fn());
    }
  }
  if (res.status === 503 && res.headers.get('retry-after') === '300')
    maintenanceListeners.forEach((fn) => fn());
  return res;
}

/** Error thrown for every non-2xx answer; `field` points at the form field for 400s. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly field?: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static async from(res: Response, body?: unknown): Promise<ApiError> {
    let b = body;
    if (b === undefined) {
      try {
        b = await res.clone().json();
      } catch {
        b = undefined;
      }
    }
    const o = (b ?? {}) as {
      error?: string;
      message?: string | string[];
      details?: { field?: string };
    };
    const message = Array.isArray(o.message)
      ? o.message.join('; ')
      : (o.message ?? res.statusText ?? 'Request failed');
    return new ApiError(res.status, o.error ?? `HTTP_${res.status}`, message, o.details?.field, b);
  }
}

/** Human message for any thrown error (network failures included). */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return e.message || 'You do not have permission to do this.';
    if (e.status === 404) return e.message || 'Not found.';
    if (e.status >= 500) return 'The server could not complete the request. Try again in a moment.';
    return e.message;
  }
  if (e instanceof TypeError) return 'Cannot reach the server. Check your connection.';
  return e instanceof Error ? e.message : String(e);
}

/** Raw authenticated request (multipart uploads, blobs). Throws ApiError on failure. */
export async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await authFetch(new Request(`${API_BASE}${path}`, init));
  if (!res.ok) throw await ApiError.from(res);
  return res;
}
